import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    draggable,
    dropTargetForElements
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import {
    attachClosestEdge,
    extractClosestEdge
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview';
import { preserveOffsetOnSource } from '@atlaskit/pragmatic-drag-and-drop/element/preserve-offset-on-source';
import './scss/AddCustomFields.scss';
import {
    fetchCustomFields,
    createCustomField,
    updateCustomField,
    deleteCustomField,
    reorderCustomFields,
    socketFieldsReordered,
    selectBoardCustomFields
} from '../../redux/Slices/customFieldsSlice';
import { showError, showSuccess } from '../../utils/snackbarHelpers';
import Spinner from '../Global/Spinner';
import DragIndicator from '../icons/DragIndicator';
import KeyboardArrowRight from '../icons/KeyboardArrowRight';
import KeyboardBackspace from '../icons/KeyboardBackspace';
import useHandlePopoverClick from '../../hooks/useHandlePopoverClick';

const FIELD_TYPES = [
    { value: 'text', label: 'Text', icon: 'short_text', hint: 'Short text value' },
    { value: 'number', label: 'Number', icon: 'tag', hint: 'Numeric value' },
    { value: 'date', label: 'Date', icon: 'calendar_month', hint: 'Date picker' },
    { value: 'dropdown', label: 'Dropdown', icon: 'arrow_drop_down_circle', hint: 'Single-select from options' },
    { value: 'checkbox', label: 'Checkbox', icon: 'check_box', hint: 'Toggle on/off' }
];

const TYPE_ICON = Object.fromEntries(FIELD_TYPES.map(t => [t.value, t.icon]));

// Same swatch set as AddLabels — keeps the color vocabulary consistent.
const COLOR_SWATCHES = [
    '#61bd4f', '#f2d600', '#ff9f1a', '#eb5a46', '#c377e0', '#0079bf', '#519839',
    '#d9b51c', '#e79217', '#b04632', '#89609e', '#055a8c', '#5aac44', '#f5dd29',
    '#ffab4a', '#ef7564', '#cd8de5', '#5ba4cf', '#29cce5', '#6deca9', '#ff8ed4',
    '#172b4d', '#4d4d4d'
];

// Trello-style suggested fields. Each template is the payload accepted by
// the create endpoint — clicking "Add" on a suggestion creates the field
// with one click. Filtered to exclude names already on the board.
const SUGGESTED_FIELDS = [
    {
        name: 'Status',
        type: 'dropdown',
        showOnFront: false,
        options: [
            { label: 'To do',       color: '#eb5a46', order: 0 },
            { label: 'In progress', color: '#ff9f1a', order: 1 },
            { label: 'In review',   color: '#c377e0', order: 2 },
            { label: 'Done',        color: '#61bd4f', order: 3 }
        ]
    },
    {
        name: 'Priority',
        type: 'dropdown',
        showOnFront: false,
        options: [
            { label: 'Low',      color: '#61bd4f', order: 0 },
            { label: 'Medium',   color: '#f2d600', order: 1 },
            { label: 'High',     color: '#ff9f1a', order: 2 },
            { label: 'Critical', color: '#eb5a46', order: 3 }
        ]
    },
    {
        name: 'Type',
        type: 'dropdown',
        showOnFront: false,
        options: [
            { label: 'Bug',         color: '#eb5a46', order: 0 },
            { label: 'Feature',     color: '#0079bf', order: 1 },
            { label: 'Task',        color: '#519839', order: 2 },
            { label: 'Improvement', color: '#c377e0', order: 3 }
        ]
    },
    {
        name: 'Story points',
        type: 'number',
        showOnFront: false,
        config: { min: 0, unit: 'pts' }
    },
    {
        name: 'Risk',
        type: 'dropdown',
        showOnFront: false,
        options: [
            { label: 'Low',    color: '#61bd4f', order: 0 },
            { label: 'Medium', color: '#f2d600', order: 1 },
            { label: 'High',   color: '#eb5a46', order: 2 }
        ]
    }
];

/* ---- List view ---- */
//
// Drag-and-drop uses the same pragmatic-drag-and-drop wiring as the board's
// card list: each row is both a draggable and a drop target with closest-edge
// detection on the top/bottom edges. While the user drags, rows shuffle into
// their target positions in place — no separate insertion indicator. On drop
// we commit the final order to the server.

const FieldRow = ({ field, fieldsRef, onPick, moveField, commitOrder }) => {
    const ref = useRef(null);
    const [dragging, setDragging] = useState(false);
    const lastMoveRef = useRef(null);

    useEffect(() => {
        const element = ref.current;
        if (!element) return;

        const cleanup = combine(
            draggable({
                element,
                dragHandle: element.querySelector('.cf-popover-drag') || element,
                getInitialData: () => ({ type: 'CUSTOM_FIELD', fieldId: field._id }),
                onGenerateDragPreview({ nativeSetDragImage, location }) {
                    setCustomNativeDragPreview({
                        nativeSetDragImage,
                        getOffset: preserveOffsetOnSource({
                            element,
                            input: location.current.input
                        }),
                        render({ container }) {
                            const rect = element.getBoundingClientRect();
                            // The popover's row styles are scoped under
                            // .add-custom-fields-popover, so we have to wrap
                            // the clone in the same ancestor for the cascade
                            // to apply once the preview is portaled to body.
                            const wrapper = document.createElement('div');
                            wrapper.className = 'add-custom-fields-popover cf-drag-preview-wrapper';
                            const list = document.createElement('ul');
                            list.className = 'cf-popover-list';
                            const preview = element.cloneNode(true);
                            preview.classList.add('is-dragging-preview');
                            preview.style.width = `${rect.width}px`;
                            preview.style.background = 'var(--popover-background-color)';
                            preview.style.borderRadius = 'var(--element-border-radius)';
                            preview.style.boxShadow = '0 6px 24px rgba(0, 0, 0, 0.28)';
                            preview.style.transform = 'rotate(3deg)';
                            list.appendChild(preview);
                            wrapper.appendChild(list);
                            container.appendChild(wrapper);
                        }
                    });
                },
                onDragStart: () => { setDragging(true); lastMoveRef.current = null; },
                onDrop: () => {
                    setDragging(false);
                    lastMoveRef.current = null;
                    // Commit final order to the server.
                    commitOrder();
                }
            }),
            dropTargetForElements({
                element,
                canDrop: ({ source }) => source.data.type === 'CUSTOM_FIELD',
                getIsSticky: () => true,
                getData: ({ input, element }) => attachClosestEdge(
                    { type: 'CUSTOM_FIELD', fieldId: field._id },
                    { input, element, allowedEdges: ['top', 'bottom'], threshold: 0.25 }
                ),
                onDrag: (args) => {
                    const { source, self } = args;
                    if (!source || !self) return;
                    if (source.data.fieldId === field._id) return;

                    const edge = extractClosestEdge(self.data);
                    if (!edge) return;

                    const fields = fieldsRef.current;
                    const fromIdx = fields.findIndex(f => f._id === source.data.fieldId);
                    const overIdx = fields.findIndex(f => f._id === field._id);
                    if (fromIdx === -1 || overIdx === -1) return;

                    const isMovingDown = fromIdx < overIdx;
                    let toIdx;
                    if (isMovingDown && edge === 'bottom') toIdx = overIdx;
                    else if (!isMovingDown && edge === 'top') toIdx = overIdx;
                    else return;

                    if (lastMoveRef.current === toIdx) return;
                    lastMoveRef.current = toIdx;
                    moveField(fromIdx, toIdx);
                }
            })
        );
        return cleanup;
    }, [field._id, fieldsRef, moveField, commitOrder]);

    return (
        <li
            ref={ref}
            className={`cf-popover-list-row ${dragging ? 'is-dragging' : ''}`}
        >
            <span className="cf-popover-drag" aria-label="Drag to reorder">
                <DragIndicator size={16} />
            </span>
            <button
                type="button"
                className="cf-popover-list-button"
                onClick={() => onPick(field._id)}
            >
                <span className="material-symbols-outlined cf-popover-list-icon">
                    {TYPE_ICON[field.type] || 'tune'}
                </span>
                <span className="cf-popover-list-name">{field.name}</span>
                <span className="cf-popover-list-chevron">
                    <KeyboardArrowRight size={16} />
                </span>
            </button>
        </li>
    );
};

const FieldList = ({ fields, onPick, onNew, onReorder, onAddSuggestion, addingSuggestion }) => {
    // Keep a ref to the latest fields array so per-row onDrag handlers always
    // see the current positions without re-registering the DnD adapter.
    const fieldsRef = useRef(fields);
    useLayoutEffect(() => { fieldsRef.current = fields; }, [fields]);

    const moveField = (fromIdx, toIdx) => {
        const next = fieldsRef.current.slice();
        const [moved] = next.splice(fromIdx, 1);
        next.splice(toIdx, 0, moved);
        // Reflect the live reorder optimistically; the row that was dropped
        // commits the final order to the server in onDrop.
        onReorder(next.map(f => f._id), { commit: false });
    };

    const commitOrder = () => {
        onReorder(fieldsRef.current.map(f => f._id), { commit: true });
    };

    // Suggestions: anything not already on the board, by name (case-insensitive).
    const takenNames = useMemo(
        () => new Set(fields.map(f => f.name.trim().toLowerCase())),
        [fields]
    );
    const suggestions = SUGGESTED_FIELDS.filter(s => !takenNames.has(s.name.toLowerCase()));

    return (
        <>
            <p className="cf-popover-intro">
                Select <strong>New field</strong> to build a custom field. Fields are added
                to every card on this board.
            </p>

            <ul className="cf-popover-list">
                {fields.map(field => (
                    <FieldRow
                        key={field._id}
                        field={field}
                        fieldsRef={fieldsRef}
                        onPick={onPick}
                        moveField={moveField}
                        commitOrder={commitOrder}
                    />
                ))}
                {fields.length === 0 && (
                    <li className="cf-popover-empty-row">No custom fields yet.</li>
                )}
            </ul>

            {suggestions.length > 0 && (
                <>
                    <div className="cf-popover-section-label">Suggested fields</div>
                    <ul className="cf-popover-suggestions">
                        {suggestions.map(s => (
                            <li key={s.name} className="cf-popover-suggestion-row">
                                <span className="material-symbols-outlined cf-popover-list-icon">
                                    {TYPE_ICON[s.type] || 'tune'}
                                </span>
                                <span className="cf-popover-list-name">{s.name}</span>
                                <button
                                    type="button"
                                    className="cf-popover-suggestion-add"
                                    onClick={() => onAddSuggestion(s)}
                                    disabled={addingSuggestion === s.name}
                                >
                                    {addingSuggestion === s.name
                                        ? <Spinner size={12} color="currentColor" />
                                        : 'Add'}
                                </button>
                            </li>
                        ))}
                    </ul>
                </>
            )}

            <button type="button" className="cf-popover-new" onClick={onNew}>
                <span className="material-symbols-outlined cf-popover-new-plus">add</span>
                New field
            </button>
        </>
    );
};

/* ---- Editor form ---- */

const FieldEditor = ({ initial, mode, onSubmit, onDelete, submitting, deleting }) => {
    const [draft, setDraft] = useState(() => ({
        name: initial?.name || '',
        type: initial?.type || 'text',
        showOnFront: !!initial?.showOnFront,
        options: initial?.options?.length
            ? initial.options.map(o => ({ _id: o._id, label: o.label, color: o.color || '' }))
            : [{ label: '', color: COLOR_SWATCHES[0] }],
        config: {
            min: initial?.config?.min ?? '',
            max: initial?.config?.max ?? '',
            unit: initial?.config?.unit ?? ''
        }
    }));

    // Track which option row's color swatch is open.
    const [openColorIdx, setOpenColorIdx] = useState(null);
    const optionRefs = useRef([]);

    // Inline delete-confirmation popover replaces the native window.confirm.
    const [confirmDelete, setConfirmDelete] = useState(false);
    useEffect(() => {
        if (!confirmDelete) return;
        const onKey = (e) => { if (e.key === 'Escape') setConfirmDelete(false); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [confirmDelete]);

    const setField = (patch) => setDraft(d => ({ ...d, ...patch }));
    const updateOption = (idx, patch) => setDraft(d => ({
        ...d, options: d.options.map((o, i) => i === idx ? { ...o, ...patch } : o)
    }));
    const addOption = (focus = true) => {
        setDraft(d => ({
            ...d, options: [...d.options, { label: '', color: COLOR_SWATCHES[d.options.length % COLOR_SWATCHES.length] }]
        }));
        if (focus) {
            // Defer to next tick so the input has rendered.
            setTimeout(() => {
                const next = optionRefs.current[draft.options.length];
                if (next) next.focus();
            }, 0);
        }
    };
    const removeOption = (idx) => setDraft(d => ({
        ...d, options: d.options.filter((_, i) => i !== idx)
    }));

    const handleOptionKeyDown = (idx) => (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (idx === draft.options.length - 1 && draft.options[idx].label.trim()) {
                addOption(true);
            } else {
                const nextInput = optionRefs.current[idx + 1];
                if (nextInput) nextInput.focus();
            }
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (submitting || deleting) return;
        const name = draft.name.trim();
        if (!name) return;

        const payload = { name, type: draft.type, showOnFront: draft.showOnFront };
        if (draft.type === 'dropdown') {
            const opts = draft.options
                .filter(o => o.label.trim())
                .map((o, i) => ({
                    ...(o._id ? { _id: o._id } : {}),
                    label: o.label.trim(),
                    color: o.color?.trim() || undefined,
                    order: i
                }));
            if (opts.length === 0) return;
            payload.options = opts;
        }
        if (draft.type === 'number') {
            const config = {};
            if (draft.config.min !== '' && Number.isFinite(Number(draft.config.min))) config.min = Number(draft.config.min);
            if (draft.config.max !== '' && Number.isFinite(Number(draft.config.max))) config.max = Number(draft.config.max);
            if (draft.config.unit?.trim()) config.unit = draft.config.unit.trim();
            if (Object.keys(config).length) payload.config = config;
        }
        onSubmit(payload);
    };

    const busy = submitting || deleting;

    return (
        <form className="cf-popover-form" onSubmit={handleSubmit}>
            <fieldset disabled={busy} className="cf-popover-fieldset">
                <label className="cf-popover-label">
                    <span>Title</span>
                    <input
                        type="text"
                        autoFocus
                        value={draft.name}
                        maxLength={80}
                        placeholder="Field name"
                        onChange={(e) => setField({ name: e.target.value })}
                    />
                </label>

                <div className="cf-popover-types">
                    <span className="cf-popover-types-label">Type</span>
                    <div className="cf-popover-types-grid">
                        {FIELD_TYPES.map(t => (
                            <button
                                type="button"
                                key={t.value}
                                className={`cf-popover-type ${draft.type === t.value ? 'selected' : ''}`}
                                disabled={mode === 'edit'}
                                onClick={() => setField({ type: t.value })}
                                title={t.hint}
                            >
                                <span className="material-symbols-outlined cf-popover-type-icon">{t.icon}</span>
                                <span className="cf-popover-type-label">{t.label}</span>
                            </button>
                        ))}
                    </div>
                    {mode === 'edit' && (
                        <small className="cf-popover-hint">Type can't be changed after creation.</small>
                    )}
                </div>

                {/* Plain label+input — the shared <Checkbox> fires onChange
                    twice per click (once via the wrapping label's onClick,
                    again via the input's native change), which flipped
                    showOnFront straight back to its previous value. */}
                <label className="cf-popover-checkbox-row">
                    <input
                        type="checkbox"
                        checked={draft.showOnFront}
                        onChange={(e) => setField({ showOnFront: e.target.checked })}
                    />
                    <span>Show on card front</span>
                </label>

                {draft.type === 'dropdown' && (
                    <div className="cf-popover-options">
                        <div className="cf-popover-options-head">
                            <span>Options</span>
                            <button type="button" className="cf-popover-link" onClick={() => addOption(true)}>
                                + Add option
                            </button>
                        </div>
                        {draft.options.map((opt, idx) => (
                            <div className="cf-popover-option-row" key={opt._id || `new-${idx}`}>
                                <span
                                    className="cf-popover-option-swatch"
                                    style={{ backgroundColor: opt.color || 'transparent' }}
                                    onClick={() => setOpenColorIdx(openColorIdx === idx ? null : idx)}
                                    role="button"
                                    aria-label="Pick color"
                                />
                                <input
                                    type="text"
                                    ref={(el) => { optionRefs.current[idx] = el; }}
                                    value={opt.label}
                                    maxLength={80}
                                    placeholder={`Option ${idx + 1}`}
                                    onChange={(e) => updateOption(idx, { label: e.target.value })}
                                    onKeyDown={handleOptionKeyDown(idx)}
                                />
                                <button
                                    type="button"
                                    className="cf-popover-option-remove"
                                    onClick={() => removeOption(idx)}
                                    disabled={draft.options.length <= 1}
                                    aria-label="Remove option"
                                >
                                    <span className="material-symbols-outlined">close</span>
                                </button>
                                {openColorIdx === idx && (
                                    <div className="cf-popover-swatch-grid">
                                        {COLOR_SWATCHES.map(c => (
                                            <span
                                                key={c}
                                                className={`cf-popover-swatch ${opt.color === c ? 'selected' : ''}`}
                                                style={{ backgroundColor: c }}
                                                onClick={() => { updateOption(idx, { color: c }); setOpenColorIdx(null); }}
                                            />
                                        ))}
                                        <span
                                            className={`cf-popover-swatch cf-popover-swatch-empty ${!opt.color ? 'selected' : ''}`}
                                            onClick={() => { updateOption(idx, { color: '' }); setOpenColorIdx(null); }}
                                            title="No color"
                                        >×</span>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {draft.type === 'number' && (
                    <div className="cf-popover-config">
                        <label className="cf-popover-label">
                            <span>Min</span>
                            <input type="number" value={draft.config.min}
                                onChange={(e) => setField({ config: { ...draft.config, min: e.target.value } })} />
                        </label>
                        <label className="cf-popover-label">
                            <span>Max</span>
                            <input type="number" value={draft.config.max}
                                onChange={(e) => setField({ config: { ...draft.config, max: e.target.value } })} />
                        </label>
                        <label className="cf-popover-label">
                            <span>Unit</span>
                            <input type="text" maxLength={16} value={draft.config.unit}
                                placeholder="e.g. pts"
                                onChange={(e) => setField({ config: { ...draft.config, unit: e.target.value } })} />
                        </label>
                    </div>
                )}
            </fieldset>

            <div className="cf-popover-form-actions">
                <button type="submit" className="cf-popover-primary" disabled={busy || !draft.name.trim()}>
                    {submitting
                        ? (<><Spinner size={14} color="#fff" /> {mode === 'edit' ? 'Saving…' : 'Creating…'}</>)
                        : (mode === 'edit' ? 'Save' : 'Create')}
                </button>
                {mode === 'edit' && (
                    <button
                        type="button"
                        className="cf-popover-danger"
                        onClick={() => setConfirmDelete(true)}
                        disabled={busy}
                    >
                        {deleting ? (<><Spinner size={14} color="currentColor" /> Deleting…</>) : 'Delete'}
                    </button>
                )}
            </div>

            {confirmDelete && (
                <div className="cf-popover-confirm-backdrop" onClick={() => !deleting && setConfirmDelete(false)}>
                    <div className="cf-popover-confirm" onClick={(e) => e.stopPropagation()}>
                        <h4>Delete this field?</h4>
                        <p>
                            “{(initial?.name || draft.name).trim() || 'this field'}” and all its
                            values on cards will be removed. This can’t be undone.
                        </p>
                        <div className="cf-popover-confirm-actions">
                            <button
                                type="button"
                                className="cf-popover-secondary"
                                onClick={() => setConfirmDelete(false)}
                                disabled={deleting}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="cf-popover-danger"
                                onClick={async () => {
                                    await onDelete();
                                    setConfirmDelete(false);
                                }}
                                disabled={deleting}
                                autoFocus
                            >
                                {deleting
                                    ? (<><Spinner size={14} color="currentColor" /> Deleting…</>)
                                    : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </form>
    );
};

/* ---- Container ---- */

const AddCustomFields = (props = {}) => {
    const dispatch = useDispatch();
    const { handlePopoverClick } = useHandlePopoverClick();
    const { card } = useSelector((state) => state.card);

    // Allow an explicit boardId prop so the same component can be mounted
    // from the BoardMenu (no card context). Falls back to the open card's
    // board when invoked from a card-level popover.
    const boardId = props.boardId || card?.board;

    // When opened from another popover (e.g. "+ Add" → Custom fields), the
    // wrapper passes isBack/backPopover so we can render a back button that
    // routes back to that popover.
    // `embedded` is set when mounted inside the BoardMenu sidebar so we drop
    // our own popover header (the BoardMenu provides one) and call the
    // optional `setActiveLabel` to drive the BoardMenu's title as the user
    // drills into new/edit views.
    // `setBackHandler` lets us register an interceptor for the BoardMenu's
    // own back arrow so it returns to the list view instead of unwinding to
    // the main menu while the user is editing/creating a field.
    const { isBack, backPopover, embedded, setActiveLabel, setBackHandler } = props;

    const fields = useSelector(state => selectBoardCustomFields(state, boardId));
    const sortedFields = useMemo(
        () => [...fields].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
        [fields]
    );

    const [view, setView] = useState('list'); // 'list' | 'new' | 'edit'
    const [editingId, setEditingId] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [addingSuggestion, setAddingSuggestion] = useState(null); // suggestion name in-flight

    useEffect(() => {
        if (boardId) dispatch(fetchCustomFields(boardId));
    }, [boardId, dispatch]);

    useEffect(() => {
        if (view === 'edit' && editingId && !sortedFields.some(f => f._id === editingId)) {
            setView('list');
            setEditingId(null);
        }
    }, [view, editingId, sortedFields]);

    // createAsyncThunk + rejectWithValue puts the actual server message on
    // result.payload; result.error.message is just 'Rejected'. Prefer payload.
    const errMsg = (result, fallback) =>
        result.payload || result.error?.message || fallback;

    const handleCreate = async (payload) => {
        setSubmitting(true);
        const result = await dispatch(createCustomField({ boardId, field: payload }));
        setSubmitting(false);
        if (result.error) {
            showError(dispatch, errMsg(result, 'Failed to create field'));
            return;
        }
        showSuccess(dispatch, 'Custom field created');
        setView('list');
    };

    const handleUpdate = async (fieldId, payload) => {
        const { type, ...patch } = payload;
        setSubmitting(true);
        const result = await dispatch(updateCustomField({ boardId, fieldId, patch }));
        setSubmitting(false);
        if (result.error) {
            showError(dispatch, errMsg(result, 'Failed to update field'));
            return;
        }
        showSuccess(dispatch, 'Custom field updated');
        setView('list');
        setEditingId(null);
    };

    const handleDelete = async (fieldId, name) => {
        // Confirmation now happens via the inline popover inside FieldEditor.
        setDeleting(true);
        const result = await dispatch(deleteCustomField({ boardId, fieldId }));
        setDeleting(false);
        if (result.error) {
            showError(dispatch, errMsg(result, 'Failed to delete field'));
            return;
        }
        showSuccess(dispatch, 'Custom field deleted');
        setView('list');
        setEditingId(null);
    };

    const handleAddSuggestion = async (suggestion) => {
        setAddingSuggestion(suggestion.name);
        const result = await dispatch(createCustomField({ boardId, field: suggestion }));
        setAddingSuggestion(null);
        if (result.error) {
            showError(dispatch, errMsg(result, 'Failed to add field'));
            return;
        }
        showSuccess(dispatch, `${suggestion.name} added`);
    };

    const handleReorder = async (orderedIds, { commit = true } = {}) => {
        // Always reflect the new order locally so rows shuffle live during drag.
        dispatch(socketFieldsReordered({ boardId, orderedIds }));
        if (!commit) return;

        const result = await dispatch(reorderCustomFields({ boardId, orderedIds }));
        if (result.error) {
            showError(dispatch, errMsg(result, 'Failed to reorder fields'));
            dispatch(fetchCustomFields(boardId));
        }
    };

    const editingField = view === 'edit' ? sortedFields.find(f => f._id === editingId) : null;
    const headerTitle = view === 'list'
        ? 'Custom Fields'
        : view === 'new'
            ? 'New Custom Field'
            : editingField?.name || 'Edit Custom Field';

    // When embedded in the BoardMenu sidebar, drive the menu's own header
    // title so it reflects the internal view (list / new / a specific field).
    useEffect(() => {
        if (!embedded || typeof setActiveLabel !== 'function') return;
        setActiveLabel(headerTitle);
    }, [embedded, setActiveLabel, headerTitle]);

    // While editing/creating in embedded mode, intercept the BoardMenu's
    // back arrow so it returns to our list view instead of leaving the
    // CustomFields screen entirely. Returning true tells the menu we
    // consumed the back action.
    useEffect(() => {
        if (!embedded || typeof setBackHandler !== 'function') return;
        if (view !== 'list') {
            setBackHandler(() => {
                if (submitting || deleting) return true;
                setView('list');
                setEditingId(null);
                return true;
            });
        } else {
            setBackHandler(null);
        }
        return () => setBackHandler(null);
    }, [embedded, setBackHandler, view, submitting, deleting]);

    // Back-button targets two different destinations depending on internal
    // view: from new/edit it returns to the list; from list (when launched
    // from another popover) it returns to that popover via handlePopoverClick.
    const showBack = view !== 'list' || (view === 'list' && isBack && backPopover);
    const handleBackHeader = !showBack
        ? null
        : (e) => {
            if (submitting || deleting) return;
            if (view !== 'list') {
                setView('list');
                setEditingId(null);
            } else if (isBack && backPopover) {
                handlePopoverClick(e, backPopover, { ...props, isBack: false, isPopPositionChanged: false });
            }
        };

    // Embedded mode: the BoardMenu already shows the title and a back arrow
    // up in its own header, and the back arrow is wired to walk our internal
    // nav via setBackHandler. So we skip our own header entirely.
    const renderHeader = () => {
        if (embedded) return null;
        return (
            <div className="popover-header cf-popover-header">
                {handleBackHeader && (
                    <button
                        type="button"
                        className="cf-popover-back"
                        aria-label="Back"
                        onClick={handleBackHeader}
                        disabled={submitting || deleting}
                    >
                        <KeyboardBackspace size={20} />
                    </button>
                )}
                <h3>{headerTitle}</h3>
            </div>
        );
    };

    return (
        <div className={`add-custom-fields-popover ${embedded ? 'is-embedded' : ''}`}>
            {renderHeader()}

            <div className="popover-content cf-popover-content">
                {view === 'list' && (
                    <FieldList
                        fields={sortedFields}
                        onPick={(id) => { setEditingId(id); setView('edit'); }}
                        onNew={() => setView('new')}
                        onReorder={handleReorder}
                        onAddSuggestion={handleAddSuggestion}
                        addingSuggestion={addingSuggestion}
                    />
                )}
                {view === 'new' && (
                    <FieldEditor mode="new" onSubmit={handleCreate} submitting={submitting} />
                )}
                {view === 'edit' && editingField && (
                    <FieldEditor
                        mode="edit"
                        initial={editingField}
                        onSubmit={(p) => handleUpdate(editingField._id, p)}
                        onDelete={() => handleDelete(editingField._id, editingField.name)}
                        submitting={submitting}
                        deleting={deleting}
                    />
                )}
            </div>
        </div>
    );
};

export default AddCustomFields;
